import React from "react";
import { ToggleButtonGroupProps } from "@mui/material/ToggleButtonGroup";
import { useController } from "react-hook-form";

import { useSmartFormContext } from "components/SmartForm";
import HelperText from "../HelperText";

import * as S from "./styles";

export type SegmentedOptionT = {
  label: string;
  value: string;
  icon?: React.ReactNode;
};

type SegmentedControlPropsT = {
  options: SegmentedOptionT[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
} & Omit<ToggleButtonGroupProps, "exclusive" | "onChange" | "value">;

// Standalone variant for forms outside a <SmartForm> context; wire it up with
// react-hook-form's useController/Controller (or any value/onChange pair).
export const SegmentedControl = ({ options, value, onChange, disabled = false, ...rest }: SegmentedControlPropsT) => {
  const handleChange = (_: React.MouseEvent<HTMLElement>, newValue: string | null) => {
    // Clicking the active segment returns null; ignore it so one option is always selected.
    if (newValue !== null) onChange(newValue);
  };

  return (
    <S.SegmentedGroup value={value} onChange={handleChange} exclusive disabled={disabled} {...rest}>
      {options.map((opt) => (
        <S.Segment value={opt.value} key={opt.value} disableRipple>
          {opt.icon}
          {opt.label}
        </S.Segment>
      ))}
    </S.SegmentedGroup>
  );
};

type SmartFormSegmentedControlPropsT = {
  name: string;
  helperText?: string;
  error?: boolean;
} & Omit<SegmentedControlPropsT, "value" | "onChange">;

const SmartFormSegmentedControl = ({ name, helperText, error, ...rest }: SmartFormSegmentedControlPropsT) => {
  const { controllerProps } = useSmartFormContext();
  const { field } = useController({ ...controllerProps, name });

  return (
    <>
      <SegmentedControl value={field.value} onChange={field.onChange} {...rest} />
      {helperText && <HelperText error={error}>{helperText}</HelperText>}
    </>
  );
};

export default SmartFormSegmentedControl;
